import { z } from 'zod'
import { steps } from '../content/steps'
import type {
  ActivityState,
  EvidenceMeta,
  TechnicalEvidence,
} from '../types'

export const STORAGE_KEY = 'recuperacao-icp:activity:v3'
export const PREVIOUS_STORAGE_KEY = 'recuperacao-icp:activity:v2'
export const LEGACY_STORAGE_KEY = 'recuperacao-icp:activity:v1'

const evidenceMetaSchema = z.object({
  id: z.string(),
  stepId: z.string(),
  name: z.string(),
  type: z.string(),
  size: z.number(),
  createdAt: z.string(),
})

const activityStateSchema = z.object({
  version: z.literal(3),
  currentStepIndex: z.number().int().min(0).max(steps.length - 1),
  furthestStepIndex: z.number().int().min(0).max(steps.length - 1),
  completedStepIds: z.array(z.string()),
  checklistByStep: z.record(z.string(), z.array(z.string())),
  fieldValues: z.record(z.string(), z.string()),
  evidenceByStep: z.record(z.string(), z.array(evidenceMetaSchema)),
  technicalEvidence: z.unknown().nullable(),
  startedAt: z.string(),
  updatedAt: z.string(),
  elapsedSecondsByStep: z.record(z.string(), z.number().nonnegative()),
  instructionProgressByStep: z.record(
    z.string(),
    z.number().int().nonnegative(),
  ),
})

export function createInitialActivityState(
  now = new Date().toISOString(),
): ActivityState {
  return {
    version: 3,
    currentStepIndex: 0,
    furthestStepIndex: 0,
    completedStepIds: [],
    checklistByStep: {},
    fieldValues: {},
    evidenceByStep: {},
    technicalEvidence: null,
    startedAt: now,
    updatedAt: now,
    elapsedSecondsByStep: {},
    instructionProgressByStep: {},
  }
}

export function loadActivityState(): ActivityState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return createInitialActivityState()
    const parsed = activityStateSchema.safeParse(JSON.parse(raw))
    if (!parsed.success) return createInitialActivityState()
    return {
      ...parsed.data,
      technicalEvidence:
        parsed.data.technicalEvidence as TechnicalEvidence | null,
    }
  } catch {
    return createInitialActivityState()
  }
}

export function persistActivityState(state: ActivityState): void {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ ...state, updatedAt: new Date().toISOString() }),
  )
}

export function setFieldValue(
  state: ActivityState,
  _stepId: string,
  fieldKey: string,
  value: string,
): ActivityState {
  return {
    ...state,
    fieldValues: {
      ...state.fieldValues,
      [fieldKey]: value,
    },
  }
}

export function setCurrentStep(
  state: ActivityState,
  requestedIndex: number,
): ActivityState {
  const safeIndex = Math.max(
    0,
    Math.min(requestedIndex, steps.length - 1),
  )
  return {
    ...state,
    currentStepIndex: safeIndex,
    furthestStepIndex: Math.max(state.furthestStepIndex, safeIndex),
  }
}

export function addElapsedSeconds(
  state: ActivityState,
  stepId: string,
  seconds: number,
): ActivityState {
  return {
    ...state,
    elapsedSecondsByStep: {
      ...state.elapsedSecondsByStep,
      [stepId]: (state.elapsedSecondsByStep[stepId] ?? 0) + seconds,
    },
  }
}

export function isStepReady(
  state: ActivityState,
  stepId: string,
): boolean {
  const step = steps.find((candidate) => candidate.id === stepId)
  if (!step) return false
  return (step.fields ?? [])
    .filter((field) => field.required)
    .every((field) => Boolean(state.fieldValues[field.key]?.trim()))
}

export function completeStep(
  state: ActivityState,
  stepId: string,
): ActivityState {
  if (!isStepReady(state, stepId)) return state
  return {
    ...state,
    completedStepIds: Array.from(
      new Set([...state.completedStepIds, stepId]),
    ),
  }
}

export function toggleChecklistItem(
  state: ActivityState,
  stepId: string,
  item: string,
): ActivityState {
  const selected = new Set(state.checklistByStep[stepId] ?? [])
  if (selected.has(item)) selected.delete(item)
  else selected.add(item)
  return {
    ...state,
    checklistByStep: {
      ...state.checklistByStep,
      [stepId]: [...selected],
    },
  }
}

export function advanceInstruction(
  state: ActivityState,
  stepId: string,
): ActivityState {
  const step = steps.find((candidate) => candidate.id === stepId)
  if (!step) return state
  const current = state.instructionProgressByStep[stepId] ?? 0
  return {
    ...state,
    instructionProgressByStep: {
      ...state.instructionProgressByStep,
      [stepId]: Math.min(step.instructions.length, current + 1),
    },
  }
}

export function setInstructionProgress(
  state: ActivityState,
  stepId: string,
  requestedIndex: number,
): ActivityState {
  const step = steps.find((candidate) => candidate.id === stepId)
  if (!step) return state
  const safeIndex = Math.max(
    0,
    Math.min(requestedIndex, step.instructions.length - 1),
  )
  return {
    ...state,
    instructionProgressByStep: {
      ...state.instructionProgressByStep,
      [stepId]: safeIndex,
    },
  }
}

export function addEvidenceMeta(
  state: ActivityState,
  meta: EvidenceMeta,
): ActivityState {
  return {
    ...state,
    evidenceByStep: {
      ...state.evidenceByStep,
      [meta.stepId]: [
        ...(state.evidenceByStep[meta.stepId] ?? []),
        meta,
      ],
    },
  }
}

export function removeEvidenceMeta(
  state: ActivityState,
  stepId: string,
  evidenceId: string,
): ActivityState {
  return {
    ...state,
    evidenceByStep: {
      ...state.evidenceByStep,
      [stepId]: (state.evidenceByStep[stepId] ?? []).filter(
        (item) => item.id !== evidenceId,
      ),
    },
  }
}

export function setTechnicalEvidence(
  state: ActivityState,
  technicalEvidence: TechnicalEvidence | null,
): ActivityState {
  return { ...state, technicalEvidence }
}

export function prepareFinalPackageState(
  state: ActivityState,
): ActivityState {
  return state
}

export function allStepsComplete(state: ActivityState): boolean {
  return state.currentStepIndex === steps.length - 1
}
